import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient, { setAppToken } from '../api/client';
import { collectAppSession } from '../utils/appSession';
import { track, identifyUser } from '../utils/analytics';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // true when we could not reach the server at all — as opposed to being told
  // we are logged out. The shell shows "can't reach MeMo, retry" for this,
  // never the login screen.
  const [unreachable, setUnreachable] = useState(false);
  const [attempt, setAttempt] = useState(0);   // bumped by the Retry button

  // Ask the server who we are, and DON'T confuse "the server didn't answer"
  // with "you are logged out".
  //
  // This used to be a bare .catch() that swallowed every failure, so a single
  // request that didn't get through — the phone's radio not ready a heartbeat
  // after the app launched, a tunnel, a moment of no signal — dropped the user
  // on the login screen although the session was perfectly valid. In the
  // installed Android app that happens on nearly every cold start, which is
  // why it felt like "MeMo logs me out every time".
  // Only a 401/403 is an answer meaning "not logged in". Anything else gets
  // retried, and the app keeps showing its loading state meanwhile.
  // Returns the user, or throws 'unreachable' when the server never answered.
  // Those two are NOT the same thing, and the difference is the whole point:
  // giving up after a few seconds and showing the login screen told a user with
  // a perfectly good session that they had been signed out. They then signed in
  // again — for a tunnel, or a moment without signal.
  async function fetchMe(attempt = 0) {
    const BACKOFF_MS = [700, 1800, 4000];
    try {
      const res = await apiClient.get('/auth/me');
      return res.data;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) return null; // genuinely logged out
      if (attempt < BACKOFF_MS.length) {
        await new Promise(r => setTimeout(r, BACKOFF_MS[attempt]));
        return fetchMe(attempt + 1);
      }
      throw new Error('unreachable');
    }
  }

  // On mount: verify session via the httpOnly cookie. The app and API share
  // the memocal.app site, so the cookie is first-party everywhere — the old
  // #token= URL-fragment fallback (token visible to history/JS) is gone.
  useEffect(() => {
    sessionStorage.removeItem('app_token'); // clean up any legacy fallback token
    setLoading(true);
    setUnreachable(false);
    // Before asking who we are: if the installed app has just been handed a
    // login (a one-time code in the fragment), spend it first. Otherwise the
    // question below is asked with no session, answers "nobody", and the app
    // shows the sign-in screen it has only this second come back from.
    // On a browser there is no fragment and this resolves immediately.
    collectAppSession()
      .then(fetchMe)
      .then((data) => {
        if (!data) return;
        const res = { data };
        setUser(res.data);

        // --- Analytics (funnel) ---------------------------------------
        // Link this browser to the person so pre-login pageviews connect up.
        identifyUser(res.data?.id, {
          language: res.data?.language,
          timezone: res.data?.timezone,
        });
        // Fire 'signup' once for a brand-new account (age < 10 min), else
        // 'login' once per browser session. Guards keep the counts honest.
        try {
          const accountNew = res.data?.created_at &&
            (Date.now() - new Date(res.data.created_at).getTime()) < 10 * 60 * 1000;
          const signupFlag = 'ph_signup_' + res.data?.id;
          if (accountNew && res.data?.id && !localStorage.getItem(signupFlag)) {
            localStorage.setItem(signupFlag, '1');
            track('signup');
          } else if (!sessionStorage.getItem('ph_login_fired')) {
            sessionStorage.setItem('ph_login_fired', '1');
            track('login');
          }
        } catch (_) {}
        // --------------------------------------------------------------

        // Auto-detect the timezone on first login: without this, new accounts
        // fell back to Europe/Rome and the morning summary fired at the wrong
        // hour abroad. Only when unset — a manual choice in Settings wins.
        if (!res.data?.timezone) {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (tz) {
            apiClient.put('/settings', { timezone: tz })
              .then(() => setUser(u => (u ? { ...u, timezone: tz } : u)))
              .catch(() => {});
          }
        }
        // Same for the language. Also treat 'en' on a brand-new account as
        // "never chosen": the users table used to default the column to 'en',
        // so the app opened in English regardless of the phone's language.
        // A manual choice in Settings always wins (account is older by then).
        const accountAgeMs = res.data?.created_at
          ? Date.now() - new Date(res.data.created_at).getTime()
          : Infinity;
        const langNeverChosen = !res.data?.language ||
          (res.data.language === 'en' && accountAgeMs < 10 * 60 * 1000);
        if (langNeverChosen) {
          const SUPPORTED = ['it','en-US','en-GB','de','fr','nl','pt-PT','pt-BR','es-ES','es-AR','es-MX','es-CO','ru','pl','ro','zh','ja','hi','ko','ar'];
          const BASE_MAP = { en: 'en-US', es: 'es-ES', pt: 'pt-PT' };
          const nav = navigator.language || '';
          const base = nav.split('-')[0].toLowerCase();
          const detected = SUPPORTED.find(l => l.toLowerCase() === nav.toLowerCase())
            || BASE_MAP[base]
            || SUPPORTED.find(l => l === base)
            || null;
          if (detected) {
            apiClient.put('/settings', { language: detected })
              .then(() => setUser(u => (u ? { ...u, language: detected } : u)))
              .catch(() => {});
          }
        }
      })
      .catch((err) => {
        // Never reached the server. Say so, instead of pretending to be logged out.
        if (err?.message === 'unreachable') setUnreachable(true);
      })
      .finally(() => setLoading(false));
  }, [attempt]);

  async function logout() {
    try {
      await apiClient.post('/auth/logout'); // clears httpOnly cookie on server
    } catch (_) {}
    // And the installed app's own token. Without this, signing out cleared the
    // cookie the app never had and left the token it actually uses — so it
    // would have stayed signed in, which is the worst possible answer to
    // somebody deliberately signing out. Cleared even if the call above failed.
    setAppToken(null);
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{
      user, setUser, logout, loading,
      unreachable,
      retryAuth: () => setAttempt(a => a + 1),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
